import { useEffect, useState, type ComponentType } from "react";

/**
 * Mounted once, everywhere: an anonymous visitor stays on localStorage, and a
 * signed-in one has their name, avatar and preferences kept on their account.
 *
 * What does that work is in `profileSyncEffect`, and it is asked for rather
 * than imported. It reaches Supabase, and Supabase's auth client is the second
 * heaviest thing the application ships; a static import from here puts it in
 * the head's module preloads on every page, in front of a first paint that
 * draws none of it (#531). Nothing is lost by starting a beat later — this
 * component renders nothing, and the account it syncs is read from a round trip
 * that was never going to have finished by the first frame anyway.
 */
export function ProfileSync() {
  const [Sync, setSync] = useState<ComponentType | null>(null);

  useEffect(() => {
    let mounted = true;
    void import("./profileSyncEffect")
      .then((module) => {
        if (mounted) setSync(() => module.ProfileSyncEffect);
      })
      .catch(() => {
        // A visitor whose profile never syncs keeps the local one, which is
        // what an anonymous visitor has anyway. Caught rather than left to
        // reject because the boot watchdog reloads the document on an unhandled
        // rejection that reads like a chunk failure.
      });
    return () => {
      mounted = false;
    };
  }, []);

  return Sync ? <Sync /> : null;
}
